import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Flag, Check, X, ExternalLink } from "lucide-react";
import { Button, EmptyState, toast } from "../ui";
import {
  discussionsApi,
  errMessage,
  formatDateTime,
  REPORT_REASONS,
} from "./api";
import "./ReportQueue.css";

const REASON_LABEL = Object.fromEntries(
  REPORT_REASONS.map((r) => [r.value, r.label])
);

/**
 * Moderator report-triage queue (spec §7.1). Lists OPEN reports — each row
 * shows the reason, the body snapshot *as it was when reported* (a later
 * edit can't hide it), the reporter, a link to the message, and Action /
 * Dismiss buttons. The server hard-enforces `require_moderator`; this view
 * is only reachable from the admin discussions surface.
 *
 * The snapshot is rendered as plain JSX text, so React HTML-escapes it —
 * a literal `<script>`/`@[…]` in the reported body is inert here (spec §8).
 */
export default function ReportQueue() {
  const [items, setItems] = useState(null);
  const [nextBefore, setNextBefore] = useState(null);
  const [error, setError] = useState("");
  const [loadingMore, setLoadingMore] = useState(false);
  const [busyId, setBusyId] = useState(null);

  const load = useCallback(() => {
    setItems(null);
    discussionsApi
      .listReports({ status: "OPEN" })
      .then((d) => {
        setItems(d.items || []);
        setNextBefore(d.next_before || null);
        setError("");
      })
      .catch((e) => {
        setItems([]);
        setError(errMessage(e));
      });
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const loadMore = () => {
    if (!nextBefore || loadingMore) return;
    setLoadingMore(true);
    discussionsApi
      .listReports({ status: "OPEN", before: nextBefore })
      .then((d) => {
        setItems((prev) => [...(prev || []), ...(d.items || [])]);
        setNextBefore(d.next_before || null);
      })
      .catch((e) => setError(errMessage(e)))
      .finally(() => setLoadingMore(false));
  };

  const resolve = async (report, action) => {
    if (busyId) return;
    setBusyId(report.id);
    try {
      await discussionsApi.resolveReport(report.id, action);
      toast.success(
        action === "ACTIONED" ? "Report actioned." : "Report dismissed."
      );
      // Resolved reports drop out of the OPEN queue.
      setItems((prev) => (prev || []).filter((r) => r.id !== report.id));
    } catch (e) {
      toast.error(errMessage(e));
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="vdsc-shell">
      <div className="vdsc-head">
        <div>
          <h1>
            <Flag size={20} /> Report queue
          </h1>
          <p className="vdsc-sub">
            Open reports awaiting moderation. Each shows the message body as it
            was when reported — a later edit can’t hide it.
          </p>
        </div>
        <div className="vdsc-head-actions">
          <Link to="/discussions" className="vdsc-rq-back">
            Back to Discussions
          </Link>
        </div>
      </div>

      {error && <div className="vdsc-error">{error}</div>}
      {items === null && <div className="vdsc-loading">Loading reports…</div>}

      {items !== null && items.length === 0 && !error && (
        <EmptyState
          icon={<Flag size={28} strokeWidth={1.4} />}
          title="No open reports"
          subtitle="When a member reports a message, it shows up here for review."
        />
      )}

      {items && items.length > 0 && (
        <div className="vdsc-rq-list">
          {items.map((r) => (
            <ReportCard
              key={r.id}
              report={r}
              busy={busyId === r.id}
              onResolve={resolve}
            />
          ))}
        </div>
      )}

      {nextBefore && (
        <div className="vdsc-loadmore">
          <Button
            variant="secondary"
            size="sm"
            onClick={loadMore}
            loading={loadingMore}
          >
            Load more
          </Button>
        </div>
      )}
    </div>
  );
}

function ReportCard({ report, busy, onResolve }) {
  const msg = report.message;
  const topic = report.topic;
  const snapshot = report.reported_body_snapshot;
  return (
    <div className="vdsc-rq-card">
      <div className="vdsc-rq-top">
        <span className="vdsc-rq-reason">
          {REASON_LABEL[report.reason] || report.reason}
        </span>
        {topic && (
          <span className="vdsc-rq-topic" title={topic.title}>
            in {topic.title}
          </span>
        )}
        <span className="vdsc-rq-time">{formatDateTime(report.created_at)}</span>
      </div>

      <div className="vdsc-rq-meta">
        <span>
          Reported by{" "}
          <strong>{report.reporter?.name || "Unknown member"}</strong>
        </span>
        {msg?.author?.name && (
          <span>
            • Message by <strong>{msg.author.name}</strong>
          </span>
        )}
        {msg?.deleted && <span className="vdsc-rq-flag">• already deleted</span>}
      </div>

      {/* Plain-text snapshot — React escapes it, so any HTML/mention markup
          in the reported body renders inert (spec §8). */}
      <blockquote className="vdsc-rq-snapshot">
        {snapshot ? snapshot : <em className="muted">No text (media only).</em>}
      </blockquote>

      {report.note && (
        <p className="vdsc-rq-note">
          <span className="vdsc-rq-note-label">Reporter’s note:</span>{" "}
          {report.note}
        </p>
      )}

      <div className="vdsc-rq-actions">
        {report.message_link && (
          <Link to={report.message_link} className="vdsc-rq-link">
            <ExternalLink size={13} /> View message
          </Link>
        )}
        <div className="vdsc-rq-actions-right">
          <Button
            variant="secondary"
            size="sm"
            disabled={busy}
            leftIcon={<X size={14} />}
            onClick={() => onResolve(report, "DISMISSED")}
          >
            Dismiss
          </Button>
          <Button
            variant="primary"
            size="sm"
            disabled={busy}
            leftIcon={<Check size={14} />}
            onClick={() => onResolve(report, "ACTIONED")}
          >
            Action
          </Button>
        </div>
      </div>
    </div>
  );
}
